import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as api from "../api/district.api";
import { DistrictPayload } from "../types/district.type";

export const DISTRICT_QUERY_KEY = ["district"];

export const useDistricts = (page = 1, limit = 10, search = "") => {
	const query = useQuery({
		queryKey: [...DISTRICT_QUERY_KEY, page, limit, search],
		queryFn: () => api.fetchDistrictList(page, limit, search),
		staleTime: 5 * 60 * 1000, // Cache for 5 minutes
		refetchOnWindowFocus: false,
		placeholderData: keepPreviousData,
	});

	return {
		...query,
		districts: query.data?.items ?? [],
		totalCount: query.data?.totalItems ?? 0,
		totalPages: query.data?.totalPages ?? 0,
	};
};

export const useDistrictById = (id: string | number) => {
	const query = useQuery({
		queryKey: ["district", id],
		queryFn: () => api.fetchDistrictById(id),
		enabled: Boolean(id),
		staleTime: 5 * 60 * 1000, // Cache for 5 minutes
		refetchOnWindowFocus: false,
	});

	return {
		...query,
		district: query.data ?? null,
	};
};

export const useCreateDistrict = () => {
	const queryClient = useQueryClient();
	const mutation = useMutation({
		mutationFn: api.createDistrict,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: DISTRICT_QUERY_KEY });
		},
	});

	return mutation;
};

export const useUpdateDistrict = () => {
	const queryClient = useQueryClient();
	const mutation = useMutation({
		mutationFn: ({
			id,
			payload,
		}: {
			id: string | number;
			payload: DistrictPayload;
		}) => api.updateDistrict(id, payload),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: DISTRICT_QUERY_KEY,
			});
		},
	});

	return mutation;
};

export const useDeleteDistrict = () => {
	const queryClient = useQueryClient();
	const mutation = useMutation({
		mutationFn: (id: string | number) => api.deleteDistrict(id),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: DISTRICT_QUERY_KEY });
		},
	});

	return mutation;
};
